import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import environment from '@environments/environment';
import { Store } from '@ngrx/store';
import { selectUser } from '@shared/store/selectors/user.selectors';
import { BehaviorSubject, map, Observable, take, tap } from 'rxjs';
import { storeUserDetail } from '@shared/store/actions/user.actions';
import { User } from '../iris-sites/interfaces/sites.interfaces';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly _http = inject(HttpClient);
  private readonly _store = inject(Store);

  private _userDetailSubject = new BehaviorSubject<User>({} as User);
  private _loadedUserDetail = false;

  userDetail$ = this._userDetailSubject.asObservable();

  user$ = this._store.select(selectUser);

  userInitials$ = this.user$.pipe(
    map(
      (user) =>
        `${user?.first_name?.charAt(0) ?? ''}${user?.last_name?.charAt(0) ?? ''}`,
    ),
  );

  getUser() {
    return this.user$.pipe(take(1));
  }

  updateUser(userDetails: any, userId: string) {
    return this._http
      .put<User>(`${environment.irisApi}/auth/user/${userId}/`, userDetails)
      .pipe(
        tap((res) => {
          this._store.dispatch(storeUserDetail({ user: res }));
        }),
      );
  }

  resetLoadedUserDetail() {
    this._loadedUserDetail = false;
  }
  getUserDetailById(userId: string): Observable<User> {
    if (this._loadedUserDetail) {
      return this.userDetail$;
    }

    this._loadedUserDetail = true;

    return this._http
      .get<User>(`${environment.irisApi}/auth/user/${userId}/`)
      .pipe(
        map((user: User) => ({
          ...user,
          data: {
            ...user.data,
            isProfileComplete: false,
            isReturnUser: false,
            showWelcomeCard: false,
            showSubmissionCard: false,
          },
        })),
        tap((user: User) => {
          this._userDetailSubject.next(user);
          this._store.dispatch(storeUserDetail({ user }));
        }),
      );
  }

  getUserContactInfo() {
    return this._http.get<any[]>(`${environment.irisApi}/auth/user/profile/`);
  }

  updateUserContactInfo(userDetails: any) {
    return this._http.put<any>(
      `${environment.irisApi}/auth/user/profile/`,
      userDetails,
    );
  }

  addOrganization(organization_id: string) {
    return this._http.post<any>(
      `${environment.irisApi}/user/organization/${organization_id}/`,
      null,
    );
  }

  removeOrganization(user_organization_id: string) {
    return this._http.delete<any>(
      `${environment.irisApi}/user/organization/${user_organization_id}/`,
    );
  }

  addUserMailingAddress(contact_id: string, address: any) {
    return this._http.post<any>(
      `${environment.irisApi}/user/profile/${contact_id}/address/`,
      address,
    );
  }

  removeUserMailingAddress(contact_id: string, address_id: string) {
    return this._http.delete<any>(
      `${environment.irisApi}/user/profile/${contact_id}/address/${address_id}/`,
    );
  }

  updateUserMailingAddress(
    contact_id: string,
    address: any,
    address_id: string,
  ) {
    return this._http.put<any>(
      `${environment.irisApi}/user/profile/${contact_id}/address/${address_id}/`,
      address,
    );
  }
}
